// PhotoSwipe 5.4.4 core and styles load on use; no high-resolution requests on initial paint.
(() => {
  if (customElements.get('product-zoom')) return;
  const styles = new Map();
  const icon = content => `<svg aria-hidden="true" class="pswp__icn tffc-zoom__icon" viewBox="0 0 32 32">${content}</svg>`;
  const closeIcon = icon('<path d="m9 9 14 14M23 9 9 23"/>');
  const arrowIcon = icon('<path d="m19 8-8 8 8 8"/>');
  const zoomIcon = icon('<circle cx="14" cy="14" r="7"/><path d="m19 19 7 7M11 14h6"/><path class="pswp__zoom-icn-bar-v" d="M14 11v6"/>');
  function loadStyle(href) {
    if (styles.has(href)) return styles.get(href);
    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = href;
      const timer = setTimeout(() => { link.remove(); reject(new Error('Zoom style timeout')); }, 8000);
      link.onload = () => { clearTimeout(timer); resolve(); };
      link.onerror = () => { clearTimeout(timer); link.remove(); reject(new Error('Zoom style failed')); };
      document.head.append(link);
    }).catch(error => { styles.delete(href); throw error; });
    styles.set(href, promise);
    return promise;
  }
  customElements.define('product-zoom', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.addEventListener('click', event => this.open(event), { signal: this.controller.signal });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.instance?.destroy();
    }
    async open(event) {
      const trigger = event.target.closest('a');
      if (!trigger || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const gallery = this.closest('product-media');
      const active = [...gallery.querySelectorAll('.product__media-item[data-media-type="image"]:not(.is-filtered)')];
      let images;
      try { images = JSON.parse(this.querySelector('[data-zoom-images]').textContent); } catch { return; }
      const items = active.map(element => {
        const item = images.find(image => String(image.id) === element.dataset.mediaId);
        return item && { ...item, element, msrc: element.querySelector('img')?.currentSrc };
      }).filter(Boolean);
      if (!items.length) return;
      const selected = items.findIndex(item => item.element.classList.contains('is-selected'));
      const index = selected >= 0 ? selected : Math.max(0, items.findIndex(item => item.element.classList.contains('is-initial-selected')));
      const fallback = items[index].src;
      event.preventDefault();
      if (this.opening || this.instance) return;
      this.opening = true;
      trigger.setAttribute('aria-busy', 'true');
      try {
        const [{ default: PhotoSwipe }] = await Promise.all([import(this.dataset.core), loadStyle(this.dataset.css), loadStyle(this.dataset.style)]);
        if (!this.isConnected) return;
        const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
        let thumbnails;
        const previewBar = ['unpublished', 'development'].includes(window.Shopify?.theme?.role) ? document.getElementById('PBarNextFrameWrapper') : null;
        const previewInset = () => { const rect = previewBar?.getBoundingClientRect(); return Math.max(0, rect?.height ? innerHeight - rect.top : 0); };
        const instance = new PhotoSwipe({
          dataSource: items, index, mainClass: 'tffc-zoom', bgOpacity: 1,
          showHideAnimationType: 'fade', showAnimationDuration: reduced ? 0 : 160, hideAnimationDuration: reduced ? 0 : 120,
          preload: navigator.connection?.saveData ? [0, 0] : [0, 1],
          closeTitle: this.dataset.close, zoomTitle: this.dataset.zoom,
          closeSVG: closeIcon, arrowPrevSVG: arrowIcon, arrowNextSVG: arrowIcon, zoomSVG: zoomIcon,
          arrowPrevTitle: this.dataset.previous, arrowNextTitle: this.dataset.next,
          paddingFn: viewport => ({ top: 72, bottom: previewInset() + (items.length > 1 ? (thumbnails?.offsetHeight || 80) + 12 : 24), left: viewport.x < 741 ? 12 : 72, right: viewport.x < 741 ? 12 : 72 }),
          trapFocus: true, returnFocus: true, clickToCloseNonZoomable: false
        });
        this.instance = instance;
        instance.on('uiRegister', () => {
          if (items.length < 2) return;
          instance.ui.registerElement({
            name: 'thumbnails', className: 'tffc-zoom__thumbnails', appendTo: 'root',
            onInit: element => {
              thumbnails = element;
              element.setAttribute('role', 'navigation');
              element.setAttribute('aria-label', this.dataset.thumbnailsLabel);
              const buttons = items.map((item, position) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'tffc-zoom__thumbnail';
                button.setAttribute('aria-label', this.dataset.slideLabel.replace('[num]', position + 1));
                const image = document.createElement('img');
                image.src = item.thumbnail || item.msrc;
                image.alt = '';
                image.width = 56; image.height = 56;
                image.loading = 'lazy'; image.decoding = 'async';
                button.append(image);
                button.addEventListener('click', () => instance.goTo(position));
                element.append(button);
                return button;
              });
              const sync = () => {
                buttons.forEach((button, position) => position === instance.currIndex ? button.setAttribute('aria-current', 'true') : button.removeAttribute('aria-current'));
                const current = buttons[instance.currIndex].getBoundingClientRect();
                const bounds = element.getBoundingClientRect();
                if (current.left < bounds.left) element.scrollLeft += current.left - bounds.left - 12;
                else if (current.right > bounds.right) element.scrollLeft += current.right - bounds.right + 12;
              };
              instance.on('change', sync);
              instance.on('resize', sync);
              instance.on('afterInit', () => { instance.updateSize(true); sync(); });
            }
          });
        });
        instance.on('resize', () => { if (thumbnails) thumbnails.style.bottom = previewInset() + 'px'; });
        if (previewBar) {
          const observer = new ResizeObserver(() => instance.updateSize(true));
          instance.on('afterInit', () => observer.observe(previewBar));
          instance.on('destroy', () => observer.disconnect());
        }
        instance.on('afterInit', () => instance.element.setAttribute('aria-label', `${this.dataset.zoom}: ${this.dataset.title}`));
        instance.on('close', () => {
          const media = items[instance.currIndex]?.element;
          if (this.isConnected && media?.isConnected && !media.classList.contains('is-filtered')) gallery.mainCarousel?.select(media);
        });
        instance.on('destroy', () => { this.instance = null; if (trigger.isConnected) trigger.focus({ preventScroll: true }); });
        trigger.focus({ preventScroll: true });
        instance.init();
      } catch {
        this.instance?.destroy();
        if (this.isConnected) location.assign(fallback);
      } finally {
        this.opening = false;
        trigger.removeAttribute('aria-busy');
      }
    }
  });
})();
